import { useCallback, useEffect, useState } from "react";
import { Archive, Download, HardDrive, Plus, RefreshCw, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import api, { apiError, can } from "../lib/api";

const formatBytes = (bytes) => {
  const value = Number(bytes || 0);
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  if (value < 1024 * 1024 * 1024) return `${(value / (1024 * 1024)).toFixed(1)} MB`;
  return `${(value / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

const statusStyle = {
  integro: "bg-emerald-50 text-emerald-700 border-emerald-200",
  incompleto: "bg-amber-50 text-amber-700 border-amber-200",
  danneggiato: "bg-red-50 text-red-700 border-red-200",
};

const statusLabel = {
  integro: "Integro",
  incompleto: "Incompleto",
  danneggiato: "Danneggiato",
};

export default function Backup() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [downloading, setDownloading] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/backups");
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(apiError(e, "Impossibile caricare i backup"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const createBackup = async () => {
    if (!can(user, "backup", "create")) return;
    setCreating(true);
    try {
      const { data } = await api.post("/backups");
      toast.success(`Backup creato: ${data.filename}`);
      await load();
    } catch (e) {
      toast.error(apiError(e, "Creazione backup non riuscita"));
    } finally {
      setCreating(false);
    }
  };

  const downloadBackup = async (item) => {
    if (!can(user, "backup", "download")) return;
    setDownloading(item.filename);
    try {
      const response = await api.get(`/backups/${encodeURIComponent(item.filename)}/download`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = item.filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("Download avviato");
    } catch (e) {
      toast.error(apiError(e, "Download backup non riuscito"));
    } finally {
      setDownloading("");
    }
  };

  return (
    <div className="space-y-4" data-testid="backup-page">
      <div className="flex flex-wrap items-start gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold">Backup</h1>
          <p className="text-sm text-slate-500 mt-1">Copia di sicurezza di database e documenti del gestionale.</p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Button variant="outline" onClick={load} disabled={loading || creating} className="rounded-full" data-testid="backup-refresh">
            <RefreshCw size={15} className={`mr-1.5 ${loading ? "animate-spin" : ""}`} /> Aggiorna
          </Button>
          {can(user, "backup", "create") && (
            <Button onClick={createBackup} disabled={creating} className="rounded-full" data-testid="backup-create">
              <Plus size={15} className="mr-1.5" /> {creating ? "Creazione..." : "Crea nuovo backup"}
            </Button>
          )}
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <div className="bg-white rounded-2xl border border-slate-100 p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center"><Archive size={19} /></div>
          <div><p className="text-xs text-slate-400">Backup disponibili</p><p className="text-xl font-bold">{items.length}</p></div>
        </div>
        <div className="bg-white rounded-2xl border border-slate-100 p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center"><ShieldCheck size={19} /></div>
          <div><p className="text-xs text-slate-400">Ultimo stato</p><p className="font-semibold">{items[0] ? (statusLabel[items[0].status] || items[0].status) : "—"}</p></div>
        </div>
        <div className="bg-white rounded-2xl border border-slate-100 p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-slate-50 text-slate-600 flex items-center justify-center"><HardDrive size={19} /></div>
          <div><p className="text-xs text-slate-400">Conservazione</p><p className="font-semibold">Ultimi 5</p></div>
        </div>
      </div>

      <div className="bg-blue-50/70 border border-blue-100 rounded-2xl px-4 py-3 text-sm text-blue-800">
        Da questa pagina puoi creare e scaricare copie di sicurezza. Il ripristino resta volutamente un'operazione tecnica lato server.
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100">
          <h2 className="font-heading font-bold">Backup disponibili</h2>
        </div>

        {loading ? (
          <div className="py-16 flex justify-center" data-testid="backup-loading">
            <div className="w-8 h-8 rounded-full border-4 border-blue-200 border-t-blue-600 animate-spin" />
          </div>
        ) : items.length === 0 ? (
          <div className="py-14 text-center" data-testid="backup-empty">
            <Archive size={30} className="mx-auto text-slate-300 mb-2" />
            <p className="text-sm text-slate-500">Nessun backup creato da questa sezione.</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100" data-testid="backup-list">
            {items.map((item) => {
              const date = new Date(item.created_at);
              const validDate = !Number.isNaN(date.getTime());
              return (
                <div key={item.filename} className="px-4 sm:px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`backup-row-${item.filename}`}>
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className="w-10 h-10 shrink-0 rounded-xl bg-slate-50 text-slate-500 flex items-center justify-center"><Archive size={18} /></div>
                    <div className="min-w-0">
                      <p className="font-medium text-sm truncate">{item.filename}</p>
                      <p className="text-xs text-slate-400 mt-0.5">
                        {validDate ? date.toLocaleDateString("it-IT") : "Data non disponibile"}
                        {validDate ? ` · ${date.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}` : ""}
                        {` · ${formatBytes(item.size)}`}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 sm:justify-end">
                    <span className={`text-xs font-medium px-2.5 py-1 rounded-full border ${statusStyle[item.status] || "bg-slate-50 text-slate-600 border-slate-200"}`}>
                      {statusLabel[item.status] || item.status || "Disponibile"}
                    </span>
                    {can(user, "backup", "download") && (
                      <button
                        type="button"
                        onClick={() => downloadBackup(item)}
                        disabled={downloading === item.filename}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium text-blue-600 hover:bg-blue-50 disabled:opacity-50"
                        data-testid={`backup-download-${item.filename}`}
                      >
                        <Download size={15} /> {downloading === item.filename ? "Download..." : "Scarica"}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
